import type { AxiosResponse } from "axios";
import axios from "axios";
import type { CSVImportData } from "csv-import-js";
import type { SerializedEditorState } from "lexical";

import {
  type AiProvider,
  type EntityName,
  EventKind,
  type FeedbackRating,
  PresignedUrlKind,
  TaskSourceKind,
  type TutorialName,
  UserRole,
} from "~/shared/enums";
import type {
  Attachment,
  AttachmentCreate,
  ChartDataResponse,
  Chat,
  Comment,
  Connector,
  DraftMessage,
  EmailAuthState,
  EventAdtl,
  FilterGroup,
  GithubIntegrationEdit,
  NotificationSettingEdit,
  PaginatedAgentMemoryList,
  PaginatedAgentScheduleList,
  PaginatedAttachmentList,
  PaginatedChatList,
  PaginatedChatMessageList,
  PaginatedCommentList,
  PaginatedDocList,
  PaginatedHelpCenterArticleList,
  PaginatedNotificationList,
  PaginatedTaskList,
  PaginatedTextSnapshotList,
  Sort,
  StreamCallback,
  SummaryStatisticsResponse,
  Task,
  TaskCreate,
  TaskField,
  TaskRelationshipQuery,
  UserTextMessage,
} from "~/shared/types";
import { useAppStore, usePageStore } from "~/stores";
import { downloadFile } from "~/utils/api";

import { requestManager } from "./requestManager";
import { DartError } from "./shared";

type Response<T> = Promise<AxiosResponse<T>>;

type RequestOptions = {
  authOverride?: boolean;
  absolute?: boolean;
  nonBlocking?: boolean;
  background?: boolean;
  abortPreviousRequest?: boolean;
  key?: string;
};

const BASE_URL = "/api/v0";

const streamRequest = async (
  responsePromise: Promise<AxiosResponse>,
  onChunk: (chunk: string) => void
): Promise<void> => {
  const response = await responsePromise;
  const reader = response.data.getReader();
  const decoder = new TextDecoder();

  let result = await reader.read();
  while (!result.done) {
    onChunk(decoder.decode(result.value, { stream: true }));
    // eslint-disable-next-line no-await-in-loop
    result = await reader.read();
  }

  // Flush any remaining buffered bytes (handles incomplete multi-byte UTF-8 sequences)
  const remaining = decoder.decode();
  if (remaining) {
    onChunk(remaining);
  }
};

export const streamPostRequest = (
  path: string,
  data: object,
  onChunk: (chunk: string) => void,
  pageStore: ReturnType<typeof usePageStore>
): Promise<void> =>
  streamRequest(
    axios.post(`${BASE_URL}/${path}`, data, {
      adapter: "fetch",
      responseType: "stream",
      headers: {
        "Content-Type": "application/json",
        "x-csrftoken": pageStore.csrftoken ?? "",
        "client-duid": pageStore.duid,
      },
    }),
    onChunk
  );

const streamGetRequest = (path: string, onChunk: (chunk: string) => void): Promise<void> =>
  streamRequest(
    axios.get(`${BASE_URL}/${path}`, {
      adapter: "fetch",
      responseType: "stream",
    }),
    onChunk
  );

const logTime = async <T>(message: string, promiseFunc: () => Promise<T>): Promise<T> => {
  const result = await promiseFunc();
  usePageStore().logTime(message, performance.now());
  return result;
};

const paginatedUrl = (
  path: string,
  offset: number,
  limit?: number,
  params?: Record<string, string | boolean | null | undefined | string[]>
) => {
  const inner = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value === null) {
      inner.append(key, "");
      return;
    }
    if (value !== undefined) {
      inner.append(key, value.toString());
    }
  });

  inner.append("offset", offset.toString());
  if (limit) {
    inner.append("limit", limit.toString());
  }
  return `${path}?${inner.toString()}`;
};

// TODO convert to class and make underscored methods TS private with # instead of _
const backendOld = {
  _checkAuth: (authOverride: boolean | undefined, method: string, path: string) => {
    const pageStore = usePageStore();

    if (pageStore.isPublic && authOverride !== true) {
      throw new DartError(`Cannot make ${method} request to ${path} in public view`);
    }
  },
  _get: (path: string, options?: RequestOptions) => {
    backendOld._checkAuth(options?.authOverride, "GET", path);
    return requestManager.get(options?.absolute ? path : `${BASE_URL}/${path}`, options);
  },
  _put: (
    path: string,
    data?: object,
    options?: RequestOptions,
    headers?: Record<string, string>,
    onStream?: StreamCallback
  ) => {
    backendOld._checkAuth(options?.authOverride, "PUT", path);
    return requestManager.put(options?.absolute ? path : `${BASE_URL}/${path}`, data, headers, onStream, options);
  },
  _post: async (
    path: string,
    data?: object,
    options?: RequestOptions,
    headers?: Record<string, string>,
    onStream?: StreamCallback
  ) => {
    backendOld._checkAuth(options?.authOverride, "GET", path);
    return requestManager.post(options?.absolute ? path : `${BASE_URL}/${path}`, data, headers, onStream, options);
  },
  _downloadFileRaw: (path: string) => downloadFile(`${BASE_URL}/${path}`),
  setCsrf: async () => {
    const pageStore = usePageStore();
    pageStore.updateCsrf();
    if (pageStore.csrftoken) {
      return;
    }

    await backendOld._get("csrf-token", { authOverride: true });
    pageStore.updateCsrf();
  },
  ping: () => backendOld._get("ping"),
  log: (toDatabase: boolean, kind: string, message: string | object) =>
    backendOld._post("log", { toDatabase, datetime: new Date().toISOString(), kind, message }),
  createEvent: (kind: EventKind, adtl?: EventAdtl, nonBlocking?: boolean) =>
    backendOld._post("create-event", { kind, adtl }, { authOverride: true, nonBlocking }),
  createPresignedUrl: (kind: PresignedUrlKind, filename: string, entityDuid?: string, formDuid?: string) =>
    backendOld._post("create-presigned-url", { kind, filename, entityDuid, formDuid }, { authOverride: true }),
  createTestError: () => backendOld._post("create-test-error"),
  getUserData: () => logTime("getting user data", () => backendOld._get("user-data", { authOverride: true })),
  getSyncChanges: (since: string) =>
    backendOld._get(`sync/changes?since=${encodeURIComponent(since)}`, { background: true }),
  auth: {
    checkInvitationToken: (tokenDuid: string) => backendOld._get(`check-invitation-token?duid=${tokenDuid}`),
    checkReferralTokenAndMaybeCreateAndLogin: (tokenId: string) =>
      backendOld._get(`check-referral-token-and-maybe-create-and-login?tokenId=${tokenId}`),
    checkResetPasswordToken: (tokenDuid: string) => backendOld._get(`check-reset-password-token?duid=${tokenDuid}`),
    login: (email: string, password: string) =>
      logTime("logging in and getting user data", () => backendOld._post("login", { email, password })),
    getSamlSsoRedirect: (email: string) => backendOld._get(`sso/saml-redirect/${email}`),
    tokenLogin: (tokenDuid: string) =>
      logTime("logging in with token and getting user data", () =>
        backendOld._post("token-login", { duid: tokenDuid })
      ),
    bypassLoginLocal: () =>
      logTime("logging in with local bypass and getting user data", () => backendOld._post("bypass-login")),
    acceptInvitation: (tokenDuid: string, name: string, password: string) =>
      logTime("accepting invitation and getting user data", () =>
        backendOld._post("accept-invitation", { tokenDuid, name, password })
      ),
    signUp: (
      email: string,
      name: string,
      password: string,
      tokenId: string,
      gtagData: Record<string, string>,
      appSumoCode: string | null,
      state: EmailAuthState
    ) => backendOld._post("sign-up", { email, name, password, tokenId, gtagData, appSumoCode, state }),
    verifyEmail: (tokenDuid: string) =>
      logTime("verifying email and getting user data", () => backendOld._post("verify-email", { tokenDuid })),
    requestPasswordReset: (email: string) => backendOld._post("request-password-reset", { email }),
    changePassword: (tokenDuid: string, newPassword: string) =>
      backendOld._post("change-password", { tokenDuid, password: newPassword }),
    authenticateWithGoogle: (
      code: string,
      redirectUri: string,
      tokenId: string | null,
      gtagData: Record<string, string>,
      appSumoCode: string | null
    ) => backendOld._post("authenticate-with-google", { code, redirectUri, tokenId, gtagData, appSumoCode }),
    getActivePreviews: () => backendOld._get("active-previews"),
    getEnvironment: () => backendOld._get("environment", { authOverride: true }),
    logout: () => backendOld._post("logout"),
    makeLoginToken: (duid: string): Response<{ loginToken: string; loginUrl: string }> =>
      backendOld._post("make-login-token", { duid }),
    makeTeammateLoginTokenAdmin: (duid?: string) =>
      backendOld._post("make-teammate-login-token-admin", { duid: duid ?? null }),
    sendDesktopLinkEmail: () => backendOld._post("send-desktop-link-email"),
  },
  agents: {
    rotateAuthToken: (agentDuid: string) => backendOld._post("agents/rotate-auth-token", { agentDuid }),
    deleteAuthToken: (agentDuid: string) => backendOld._post("agents/delete-auth-token", { agentDuid }),
    disconnectLocal: (agentDuid: string) => backendOld._post("agents/disconnect-local", { agentDuid }),
    uploadImage: async (agentDuid: string, file: File) => {
      const response = await backendOld.createPresignedUrl(PresignedUrlKind.PROFILE, file.name);
      await backendOld._put(
        response.data.presignedUrl,
        file,
        { absolute: true, background: true },
        response.data.signedHeaders
      );
      await backendOld._post("agents/complete-image-upload", {
        agentDuid,
        imagePath: response.data.filePath,
        contentType: file.type,
      });
      return response;
    },
    removeImage: (agentDuid: string) => backendOld._post("agents/remove-image", { agentDuid }),
    generateImage: (agentDuid: string) =>
      backendOld._post("agents/generate-image", { agentDuid }, { background: true }),
    generateDescription: (agentDuid: string) => backendOld._post("agents/generate-description", { agentDuid }),
    rotateWebhook: (agentDuid: string) => backendOld._post("agents/rotate-webhook", { agentDuid }),
    deleteWebhook: (agentDuid: string) => backendOld._post("agents/delete-webhook", { agentDuid }),
    stopSession: (sessionDuid: string) => backendOld._post("agents/stop-session", { sessionDuid }),
    stopAllSessions: (agentDuid: string) => backendOld._post("agents/stop-all-sessions", { agentDuid }),
    runSchedule: (scheduleDuid: string): Response<{ chatDuid: string }> =>
      backendOld._post("agents/run-schedule", { scheduleDuid }),
    getHistory: (agentDuid: string, offset: number, limit?: number) =>
      backendOld._get(paginatedUrl(`agents/history/${agentDuid}`, offset, limit), { background: true }),
  },
  appSumo: {
    redeemLicense: (code: string) => backendOld._post("app-sumo/redeem-license", { code }),
  },
  dartboards: {
    getBySpace: (spaceDuid: string) => backendOld._get(`dartboards/get-by-space/${spaceDuid}`),
    replicate: (dartboardDuid: string, options: { targetDuid?: string }) =>
      backendOld._post(`dartboards/replicate/${dartboardDuid}`, options),
  },
  dashboards: {
    getDartboardStats: (dartboardDuid: string) =>
      backendOld._get(`dashboards/get-dartboard-stats/${dartboardDuid}`, { background: true }),
    getChartData: (dashboardDuid: string, key?: string): Response<ChartDataResponse> =>
      backendOld._get(`dashboards/get-chart-data/${dashboardDuid}`, {
        background: true,
        abortPreviousRequest: !!key,
        key,
      }),
    getSuperuserDashboard: (): Response<unknown> =>
      backendOld._get("dashboards/superuser-dashboard", { background: true }),
    refreshSuperuserDashboard: (): Response<unknown> =>
      backendOld._post("dashboards/superuser-dashboard/refresh", {}, { background: true }),
  },
  views: {
    getData: (viewDuid: string, all: boolean) =>
      backendOld._get(`views/get-data/${viewDuid}?all=${all}`, { authOverride: true }),
    importTemplateView: (viewDuid: string, dartboardDuid: string, propertyDuidRemap: Record<string, string>) =>
      backendOld._post("views/import-template-view", { viewDuid, dartboardDuid, propertyDuidRemap }),
  },
  docs: { getByFolder: (folderDuid: string) => backendOld._get(`docs/get-by-folder/${folderDuid}`) },
  folders: {
    getBySpace: (spaceDuid: string) => backendOld._get(`folders/get-by-space/${spaceDuid}`),
    replicate: (folderDuid: string, options: { targetDuid?: string }) =>
      backendOld._post(`folders/replicate/${folderDuid}`, options),
  },
  forms: { getData: (formDuid: string) => backendOld._get(`forms/get-data/${formDuid}`, { authOverride: true }) },
  tasks: {
    createFromExternalForm: (task: TaskCreate, attachments: AttachmentCreate[]) =>
      backendOld._post(
        "tasks/create-from-external-form",
        { task: { ...task, sourceKind: TaskSourceKind.EXTERNAL_FORM }, attachments },
        { authOverride: true }
      ),
    getByDartboard: (dartboardDuid: string) => backendOld._get(`tasks/get-by-dartboard/${dartboardDuid}`),
    notifyCommentAgents: (commentDuid: string, agentDuids: string[]): Response<{ success: boolean }> =>
      backendOld._post("tasks/notify-comment-agents", { commentDuid, agentDuids }),
    publishInitialCommentAgentView: (
      commentDuid: string,
      text: SerializedEditorState
    ): Response<{ success: boolean; isInitialComment: boolean }> =>
      backendOld._post("tasks/publish-initial-comment-agent-view", { commentDuid, text }),
    getMostRecentCommentForTasks: (
      taskDuids: string[],
      publicViewDuid?: string
    ): Response<{ result: Record<string, Comment | null> }> => {
      const params = new URLSearchParams();
      params.append("task_duids", taskDuids.join(","));
      let url = "tasks/get-most-recent-comment-for-tasks";
      if (publicViewDuid) {
        params.append("view_duid", publicViewDuid);
        url = "tasks/get-public-view-most-recent-comment-for-tasks";
      }
      return backendOld._get(`${url}?${params.toString()}`, {
        background: true,
        authOverride: publicViewDuid ? true : undefined,
      });
    },
    getCoverImageAttachmentForTasks: (
      taskDuids: string[],
      publicViewDuid?: string
    ): Response<{ result: Record<string, Attachment | null> }> => {
      const params = new URLSearchParams();
      params.append("task_duids", taskDuids.join(","));
      let url = "tasks/get-cover-image-attachment-for-tasks";
      if (publicViewDuid) {
        params.append("view_duid", publicViewDuid);
        url = "tasks/get-public-view-cover-image-attachment-for-tasks";
      }
      return backendOld._get(`${url}?${params.toString()}`, {
        background: true,
        authOverride: publicViewDuid ? true : undefined,
      });
    },
    queryRelationships: (
      items: TaskRelationshipQuery[],
      publicViewDuid?: string
    ): Response<{ result: Record<string, Task[] | null> }> => {
      const url = publicViewDuid ? "tasks/get-public-view-relationships" : "tasks/query-relationships";
      return backendOld._post(
        url,
        { items, viewDuid: publicViewDuid },
        {
          background: true,
          authOverride: publicViewDuid ? true : undefined,
        }
      );
    },
    emptyTrash: () => backendOld._get("tasks/empty-trash"),
    getHistory: (taskDuid: string, params?: { propertyDuid?: string; value?: string }) => {
      const query = new URLSearchParams();
      if (params?.propertyDuid) {
        query.set("propertyDuid", params.propertyDuid);
      }
      if (params?.value) {
        query.set("value", params.value);
      }
      const suffix = query.size > 0 ? `?${query.toString()}` : "";
      return backendOld._get(`tasks/history/${taskDuid}${suffix}`, { background: true });
    },
  },
  recommendations: {
    provideFeedback: (duid: string, rating: FeedbackRating) =>
      backendOld._post(`recommendations/feedback/${duid}`, { rating }),
    provideTaskDuplicatesFeedback: (taskDuid: string, duplicateDuids: string[], rating: FeedbackRating) =>
      backendOld._post("recommendations/task-duplicates-feedback", { taskDuid, duplicateDuids, rating }),
    getTaskDuplicates: (taskDuid: string) => backendOld._get(`recommendations/task-duplicates/${taskDuid}`),
    getTaskProperties: (duid: string, fields: TaskField[], newTitle?: string | null) =>
      backendOld._post(`recommendations/task-properties/${duid}`, { newTitle: newTitle ?? null, fields }),
    getFilters: (text: string, includeQuery?: boolean) =>
      backendOld._post("recommendations/filters", { text, includeQuery }),
    getFiltersSummary: (layoutDuid: string) => backendOld._post("recommendations/filters-summary", { layoutDuid }),
    getPageEmoji: (duid: string, newTitle?: string | null) =>
      backendOld._post(`recommendations/page-emoji/${duid}`, { newTitle: newTitle ?? null }),
    getPageIcon: (duid: string, newTitle?: string | null) =>
      backendOld._post(`recommendations/page-icon/${duid}`, { newTitle: newTitle ?? null }),
  },
  chat: {
    startBuildMode: (
      sourceId: string
    ): Response<
      | { success: true; needsAuth: true; connector: Connector; authorizationUrl: string }
      | {
          success: true;
          needsAuth: false;
          connector: Connector;
          chat: Chat;
          draft: DraftMessage;
          starterMessage: string;
        }
      | { success: false; errors?: string[] }
    > => backendOld._post("chats/start-build-mode", { sourceId }),
    streamMessage: async (
      message: UserTextMessage,
      chatDuid: string,
      onChunk: (chunk: string) => void
    ): Promise<void> => {
      const pageStore = usePageStore();
      await streamPostRequest(`chats/${chatDuid}/send`, { message }, onChunk, pageStore);
    },
    streamInProgress: (chatDuid: string, onChunk: (chunk: string) => void) =>
      streamGetRequest(`chats/${chatDuid}/stream`, onChunk),
    startDemoChat: (): Response<{ chat: Chat }> => backendOld._post("chats/demo", {}),
    streamDemoTeammates: async (chatDuid: string, onChunk: (chunk: string) => void): Promise<void> => {
      const pageStore = usePageStore();
      await streamPostRequest("chats/demo", { chatDuid }, onChunk, pageStore);
    },
    setRead: (chatDuid: string, read: boolean) =>
      backendOld._post(`chats/${chatDuid}/read`, { read }, { background: true }),
    stopStreaming: (chatDuid: string) => backendOld._post(`chats/${chatDuid}/stop-streaming`, {}, { background: true }),
    getMessageSpeechUrl: (messageDuid: string) => `${BASE_URL}/chats/messages/${messageDuid}/speech`,
  },
  speechToText: {
    transcribe: (audio: FormData) => backendOld._post("speech-to-text/transcribe", audio, {}, {}),
  },
  profile: {
    changePassword: (newPassword: string, oldPassword?: string) =>
      backendOld._post("profiles/change-password", { newPassword, oldPassword: oldPassword ?? null }),
    rotateAuthToken: () => backendOld._post("profiles/rotate-auth-token"),
    deleteAuthToken: () => backendOld._post("profiles/delete-auth-token"),
    rotateCalendarLink: () => backendOld._post("profiles/rotate-calendar-link"),
    deleteCalendarLink: () => backendOld._post("profiles/delete-calendar-link"),
    updateTutorialStatuses: (updates: { name: TutorialName; status: number }[]) =>
      backendOld._post("profiles/update-tutorial-statuses", { items: updates }),
    edit: (field: string, value: string | boolean | number | null) =>
      backendOld._post("profiles/edit", { items: [{ field, value }] }),
    uploadImage: async (file: File) => {
      const response = await backendOld.createPresignedUrl(PresignedUrlKind.PROFILE, file.name);
      await backendOld._put(
        response.data.presignedUrl,
        file,
        { absolute: true, background: true },
        response.data.signedHeaders
      );
      await backendOld._post("profiles/complete-image-upload", {
        imagePath: response.data.filePath,
        contentType: file.type,
      });
      return response;
    },
    removeImage: () => backendOld._post("profiles/remove-image"),
    generateImage: () => backendOld._post("profiles/generate-image"),
    generateDescription: () => backendOld._post("profiles/generate-description"),
    contactSupport: (text: SerializedEditorState) => backendOld._post("profiles/contact-support", { text }),
    deleteAccount: () => backendOld._post("profiles/delete"),
  },
  workspace: {
    edit: (field: string, value: string | boolean | null) =>
      backendOld._post("workspaces/edit", { items: [{ field, value }] }),
    updateAiApiKey: (provider: AiProvider, apiKey: string) =>
      backendOld._post("workspaces/update-ai-api-key", { provider, apiKey }),
    rotateWebhookSecret: () => backendOld._post("workspaces/rotate-webhook-secret"),
    finishOnboarding: (name: string, adtl: Record<string, string | null>) =>
      backendOld._post("workspaces/edit", {
        items: [
          { field: "name", value: name },
          { field: "adtl", value: adtl },
        ],
      }),
    uploadImage: async (file: File) => {
      const response = await backendOld.createPresignedUrl(PresignedUrlKind.WORKSPACE, file.name);
      await backendOld._put(
        response.data.presignedUrl,
        file,
        { absolute: true, background: true },
        response.data.signedHeaders
      );
      await backendOld._post("workspaces/complete-image-upload", {
        imagePath: response.data.filePath,
        contentType: file.type,
      });
      return response;
    },
    enableSamlSso: () => backendOld._post("workspaces/sso/enable-saml"),
    disableSamlSso: () => backendOld._post("workspaces/sso/disable-saml"),
    configureSamlSso: (options: { url?: string; xml?: string }) =>
      backendOld._post("workspaces/sso/configure-saml", { url: options.url ?? null, xml: options.xml ?? null }),
    removeImage: () => backendOld._post("workspaces/remove-image"),
    inviteUsers: (role: UserRole, emails: string[]) => backendOld._post("workspaces/invite-users", { role, emails }),
    reinviteUsers: (duids: string[]) => backendOld._post("workspaces/reinvite-users", { duids }),
    removeUsers: (duids: string[]) => backendOld._post("workspaces/remove-users", { duids }),
    import: (data: CSVImportData, source?: string) =>
      backendOld._post("workspaces/import", { data, source: source ?? null }),
    export: () => backendOld._post("workspaces/export"),
  },
  billing: {
    createCheckoutSession: (tier: string, period: string, returnPath: string) =>
      backendOld._post("billing/create-checkout-session", { tier, period, returnPath }),
    createPortalSession: (returnPath: string) => backendOld._post("billing/create-portal-session", { returnPath }),
    updateSubscriptionTier: (tier: string, period: string, returnPath: string) =>
      backendOld._post("billing/update-subscription-tier", { tier, period, returnPath }),
    resetAccount: () => backendOld._post("billing/reset-account"),
    deleteSubscriptions: () => backendOld._post("billing/delete-subscriptions"),
  },
  notifications: {
    edit: (edits: NotificationSettingEdit[]) => backendOld._post("notifications/edit", { items: edits }),
    spaceMute: (spaceDuid: string, muted: boolean) =>
      backendOld._post("notifications/space-mute", { spaceDuid, muted }),
    markAllRead: (): Response<{ success: boolean }> => backendOld._post("notifications/mark-all-read"),
  },
  notion: {
    enableIntegration: (code: string | null) => backendOld._post("notion/enable", { code }),
    disableIntegration: () => backendOld._post("notion/disable"),
  },
  slack: {
    enableIntegration: () => backendOld._post("slack/enable"),
    getInstallationLink: () => `${BASE_URL}/slack/install`,
    disableIntegration: () => backendOld._post("slack/disable"),
  },
  discord: {
    edit: (field: "enabled" | "webhookUrl", value: string | boolean | null) =>
      backendOld._post("discord/edit", { items: [{ field, value }] }),
  },
  connectors: {
    oauthConnect: (connectorDuid: string, sharedWithWorkspace: boolean, replaceWorkspaceAuth = false) =>
      backendOld._post("connectors/oauth/connect", { connectorDuid, sharedWithWorkspace, replaceWorkspaceAuth }),
    oauthDisconnect: (connectorDuid: string, sharedWithWorkspace: boolean) =>
      backendOld._post("connectors/oauth/disconnect", { connectorDuid, sharedWithWorkspace }),
  },
  zapier: {
    edit: (field: "enabled", value: boolean) => backendOld._post("zapier/edit", { items: [{ field, value }] }),
  },
  vcs: {
    enableGithubIntegration: (installationId: string | null) =>
      backendOld._post("vcs/github/enable", { installation_id: installationId }),
    disableGithubIntegration: () => backendOld._post("vcs/github/disable"),
    updateGithubSettings: (edits: GithubIntegrationEdit[]) => backendOld._post("vcs/github/edit", { items: edits }),
    onBranchLinkCopy: (taskDuid: string) => backendOld._post("vcs/copy-branch-link", { duid: taskDuid }),
  },
  entityList: {
    agentMemory: (agentDuid: string, offset: number, limit?: number): Response<PaginatedAgentMemoryList> =>
      backendOld._get(paginatedUrl("agent-memories", offset, limit, { agent_duid: agentDuid }), { background: true }),
    agentSchedule: (agentDuid: string, offset: number, limit?: number): Response<PaginatedAgentScheduleList> =>
      backendOld._get(paginatedUrl("agent-schedules", offset, limit, { agent_duid: agentDuid }), { background: true }),
    chat: (
      filters: {
        duids?: string[];
        rootTaskDuid?: string;
        search?: string;
        sourceSkillDuid?: string;
        sourceAgentScheduleDuid?: string;
        targetUserDuid?: string;
      },
      offset: number,
      limit?: number
    ): Response<PaginatedChatList> =>
      backendOld._get(
        paginatedUrl("chats", offset, limit, {
          duids: filters.duids,
          root_task_duid: filters.rootTaskDuid,
          s: filters.search,
          source_skill_duid: filters.sourceSkillDuid,
          source_agent_schedule_duid: filters.sourceAgentScheduleDuid,
          target_user_duid: filters.targetUserDuid,
        }),
        { background: true }
      ),
    chatMessage: (
      filters: { chatDuid?: string; isDraft?: boolean; kind?: string; order?: string },
      offset: number,
      limit?: number
    ): Response<PaginatedChatMessageList> =>
      backendOld._get(
        paginatedUrl("chat-messages", offset, limit, {
          chat_duid: filters.chatDuid,
          is_draft: filters.isDraft,
          kind: filters.kind,
          o: filters.order,
        }),
        { background: true }
      ),
    queryTasks: (
      filterGroup: FilterGroup,
      sorts: Sort[],
      search: string,
      offset: number,
      options?: {
        hierarchical?: boolean;
        absentees?: boolean;
        groupByPropertyDuid?: string | null;
        limit?: number;
        onlyTrash?: boolean;
        includeTrashed?: boolean;
      }
    ): Response<PaginatedTaskList> =>
      backendOld._post(
        "tasks/query",
        {
          filterGroup,
          sorts,
          search,
          pagination: { offset, limit: options?.limit },
          hierarchical: options?.hierarchical ?? false,
          absentees: options?.absentees ?? false,
          groupByPropertyDuid: options?.groupByPropertyDuid ?? null,
          onlyTrash: options?.onlyTrash ?? false,
          includeTrashed: options?.includeTrashed ?? false,
        },
        { background: true }
      ),
    getPublicViewTasks: (
      viewDuid: string,
      extraFilterGroup: FilterGroup,
      offset: number,
      options?: { hierarchical?: boolean; absentees?: boolean; groupByPropertyDuid?: string | null; limit?: number }
    ): Response<PaginatedTaskList> =>
      backendOld._post(
        "tasks/get-public-view-tasks",
        {
          viewDuid,
          extraFilterGroup,
          pagination: { offset, limit: options?.limit },
          hierarchical: options?.hierarchical ?? false,
          absentees: options?.absentees ?? false,
          groupByPropertyDuid: options?.groupByPropertyDuid ?? null,
        },
        { background: true, authOverride: true }
      ),
    getSummaryStatistics: (
      filterGroup: FilterGroup,
      options?: {
        search?: string;
        absentees?: boolean;
        groupByPropertyDuid?: string | null;
        subgroupByPropertyDuid?: string | null;
        onlyTrash?: boolean;
        includeTrashed?: boolean;
      }
    ): Response<SummaryStatisticsResponse> =>
      backendOld._post(
        "tasks/summary-statistics",
        {
          filterGroup,
          search: options?.search ?? "",
          absentees: options?.absentees ?? false,
          groupByPropertyDuid: options?.groupByPropertyDuid ?? null,
          subgroupByPropertyDuid: options?.subgroupByPropertyDuid ?? null,
          onlyTrash: options?.onlyTrash ?? false,
          includeTrashed: options?.includeTrashed ?? false,
        },
        { background: true }
      ),
    getPublicViewSummaryStatistics: (
      viewDuid: string,
      extraFilterGroup: FilterGroup,
      options?: { absentees?: boolean; groupByPropertyDuid?: string | null; subgroupByPropertyDuid?: string | null }
    ): Response<SummaryStatisticsResponse> =>
      backendOld._post(
        "tasks/get-public-view-summary-statistics",
        {
          viewDuid,
          extraFilterGroup,
          absentees: options?.absentees ?? false,
          groupByPropertyDuid: options?.groupByPropertyDuid ?? null,
          subgroupByPropertyDuid: options?.subgroupByPropertyDuid ?? null,
        },
        { background: true, authOverride: true }
      ),
    comment: (
      filters: {
        authorDuid?: string;
        order?: string;
        isDraft?: boolean;
        rootDuid?: string | null;
        taskDuid?: string;
      },
      offset: number,
      limit?: number
    ): Response<PaginatedCommentList> => {
      const appStore = useAppStore();
      const pageStore = usePageStore();
      const publicViewDuid = pageStore.isPublicView && appStore.currentPage ? appStore.currentPage.duid : undefined;
      let url = "comments";
      if (publicViewDuid) {
        url = "tasks/get-public-view-comments";
      }

      const { taskDuid, authorDuid, rootDuid, isDraft, order } = filters;
      return backendOld._get(
        paginatedUrl(url, offset, limit, {
          author_duid: authorDuid,
          o: order,
          is_draft: isDraft,
          root_duid: rootDuid,
          task_duid: taskDuid,
          view_duid: publicViewDuid,
        }),
        { background: true, authOverride: publicViewDuid ? true : undefined }
      );
    },
    attachment: (taskDuid: string, offset: number, limit?: number): Response<PaginatedAttachmentList> => {
      const appStore = useAppStore();
      const pageStore = usePageStore();
      const publicViewDuid = pageStore.isPublicView && appStore.currentPage ? appStore.currentPage.duid : undefined;
      let url = "attachments";
      if (publicViewDuid) {
        url = "tasks/get-public-view-attachments";
      }

      return backendOld._get(
        paginatedUrl(url, offset, limit, {
          task_duid: taskDuid,
          view_duid: publicViewDuid,
        }),
        { background: true, authOverride: publicViewDuid ? true : undefined }
      );
    },
    doc: (
      filters: { folderDuid?: string; title?: string; search?: string; order?: string },
      offset: number,
      options?: { limit?: number; key?: string }
    ): Response<PaginatedDocList> => {
      const { search, folderDuid, order, title } = filters;
      return backendOld._get(
        paginatedUrl("docs", offset, options?.limit, {
          folder_duid: folderDuid,
          title,
          s: search,
          o: order,
        }),
        { background: true, abortPreviousRequest: !!options?.key, key: options?.key }
      );
    },
    task: (
      filters: { title?: string; duids?: string[]; inTrash?: boolean; isDraft?: boolean; isCompleted?: boolean },
      offset: number,
      options?: { limit?: number; key?: string }
    ): Response<PaginatedTaskList> => {
      const { title, duids, inTrash, isDraft, isCompleted } = filters;
      const qparams: {
        title?: string;
        in_trash?: boolean;
        is_draft?: boolean;
        is_completed?: boolean;
        duids?: string[];
      } = {
        title,
        in_trash: inTrash,
        is_draft: isDraft,
        is_completed: isCompleted,
      };
      if (duids?.length) {
        qparams.duids = duids;
      }
      const url = paginatedUrl("tasks", offset, options?.limit, qparams);

      return backendOld._get(url, {
        background: true,
        abortPreviousRequest: !!options?.key,
        key: options?.key,
      });
    },
    notification: (
      filters: { read?: boolean },
      offset: number,
      limit?: number
    ): Response<PaginatedNotificationList> => {
      const { read } = filters;
      return backendOld._get(paginatedUrl("notifications", offset, limit, { read }), { background: true });
    },
    textSnapshot: (
      filters: { entityName: EntityName; entityDuid: string },
      offset: number,
      limit?: number
    ): Response<PaginatedTextSnapshotList> =>
      backendOld._get(
        paginatedUrl("text-snapshots", offset, limit, {
          entity_name: filters.entityName,
          entity_duid: filters.entityDuid,
        }),
        { background: true }
      ),
    helpCenterArticle: (
      filters: { q?: string },
      offset: number,
      options?: { limit?: number; key?: string }
    ): Response<PaginatedHelpCenterArticleList> => {
      const { q } = filters;
      return backendOld._get(paginatedUrl("help-center-articles", offset, options?.limit, { q }), {
        background: true,
        abortPreviousRequest: !!options?.key,
        key: options?.key,
      });
    },
  },
};

export default backendOld;
